import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import { Video, Wifi, ShieldCheck, Clock, Smartphone, MapPin, Check } from "lucide-react";

const benefits = [
 { icon: Clock, title: "Less time, more care", desc: "No commutes, no waiting rooms, your time goes to the work that matters." },
 { icon: MapPin, title: "Care from anywhere safe", desc: "Bedroom, parked car, lunch break at your desk, wherever you feel comfortable." },
 { icon: Smartphone, title: "On any device", desc: "Phone, tablet, or laptop. A working camera, microphone, and stable internet are all you need." },
 { icon: ShieldCheck, title: "Encrypted & private", desc: "HIPAA-aligned video and messaging. Visible only to you and your care team." },
];

const checklist = [
 "A quiet, private space",
 "A reliable internet connection",
 "A device with camera & mic",
 "Your most recent prescriptions (if any)",
 "Notes on what you'd like to discuss",
];

export default function Telehealth() {
 return (
 <SiteLayout>
 <SEO
 title="Maryland Telepsychiatry Services"
 description="Secure, HIPAA-compliant video psychiatry from home. Same clinical depth as in-person visits, without the drive. Serving all Maryland residents statewide."
 path="/telehealth"
 />
 <section data-testid="th-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-16">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Telehealth services</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 Premium psychiatric care, <span className="italic text-sage-600">delivered to wherever you feel safe.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-xl">
 Telepsychiatry isn't a downgrade, it's how we make excellent care reachable. Same clinical depth, same continuity, same warmth.
 </p>
 </div>
 <div className="lg:col-span-5">
 <div className="relative">
 <img
 src="/telehealth-hero.png"
 alt="Telehealth, compassionate care, wherever you are. Quality mental health support from the comfort and privacy of your home."
 className="rounded-3xl w-full h-auto object-contain shadow-[0_30px_60px_-30px_rgba(26,43,51,0.3)] bg-cream"
 />
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 </div>
 </div>
 </div>
 </section>

 <section data-testid="th-benefits" className="bg-paper border-y border-[#E8EBE4]">
 <div className="container-pad max-w-7xl mx-auto py-20">
 <div className="max-w-2xl">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Why telehealth</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink text-balance">
 The convenience of virtual. The depth of in-person.
 </h2>
 </div>
 <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
 {benefits.map((b, i) => (
 <div key={b.title} data-testid={`th-benefit-${i}`} className="rounded-3xl bg-cream p-6 border border-[#E8EBE4]">
 <div className="w-11 h-11 rounded-2xl bg-sage-50 text-sage-700 flex items-center justify-center">
 <b.icon className="w-5 h-5" strokeWidth={1.5} />
 </div>
 <h3 className="mt-5 font-heading text-lg font-semibold text-ink">{b.title}</h3>
 <p className="mt-2 text-sm text-muted_text leading-relaxed">{b.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 <section data-testid="th-prep" className="container-pad max-w-7xl mx-auto section">
 <div className="grid lg:grid-cols-12 gap-12">
 <div className="lg:col-span-5">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Prepare for your visit</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
 A simple checklist for your first telehealth appointment.
 </h2>
 <p className="mt-5 text-muted_text leading-relaxed max-w-md">
 We'll send a calming, distraction-free video link a few minutes before your appointment. No app downloads required.
 </p>
 </div>
 <div className="lg:col-span-7">
 <ul className="grid sm:grid-cols-2 gap-4">
 {checklist.map((c, i) => (
 <li key={c} data-testid={`th-prep-${i}`} className="card-soft p-5 inline-flex items-start gap-3 text-ink">
 <span className="w-7 h-7 rounded-full bg-sage-500/20 text-sage-700 inline-flex items-center justify-center"><Check className="w-4 h-4" strokeWidth={2.25} /></span>
 <span className="text-sm leading-relaxed pt-0.5">{c}</span>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </section>

 <CTABanner headline="Telehealth in your time zone." sub="Evening and weekend slots are available, book the first one that works for you." />
 </SiteLayout>
 );
}
